/* eslint-disable @typescript-eslint/ban-ts-comment */
import { adaptAnswers } from 'adaptors/adaptAnswers';
import Preloader from 'components/Preloader';

import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Infobox, Loader, notification } from 'ui-library';
import { calculateStrategyScore, getClientId } from 'utils';

import { InvestorProfileWithProvider } from './InvestorProfileWithProvider';
import './InvestorProfile.css';

export const InvestorProfilesContainer = (props) => {
    const { t, i18n: { language } } = useTranslation();
    const { dataSource } = props;

    const {
        isPk, isJointAccount,
        isUsersLoading, saveAdditionalData,
        productDescription,
        clientAge,
        childClientName,
        targetClientName,
        targetDFSClientId,
        saveAdvisoryDataAction,
        advisoryProcessInProgressSelector,
        isBackDisabled,
    } = dataSource();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSending, setSending] = useState(false);
    const [profiles, setProfiles] = useState({});

    // Handlers/Callbacks

    const onQuestionnaireUpdate = useCallback((key, value) => {
        setProfiles({
            ...profiles,
            [key]: value,
        });
    }, []);

    // Handlers/Callbacks
    const onContinue = useCallback(async () => {
        try {
            setIsSubmitted(true);
            const allQuestionsFilled = Object.keys(profiles).map(key => profiles?.[key]?.allQuestionsFilled);

            if (!allQuestionsFilled?.some(a => a === false)) {
                const clientRiskToAccept = Math.max(...Object.keys(profiles).map(dfsId => getClientId(dfsId) as number));

                setSending(true);
                await Promise.all(Object.keys(profiles).map(async (key) => {
                    const {
                        clientId: mClientId,
                        data,
                        fullData,
                        formData,
                        groupId,
                        isDataChanged,
                        postQuestionnaire,
                        handlePostExternalQuestionnaire,
                        calculateRisk,
                        setChosenRisk,
                    } = profiles?.[key];

                    const answers = adaptAnswers({ data, values: formData, groupId });
                    const isQChanged = isDataChanged();

                    let response;

                    if (isQChanged) {
                        await postQuestionnaire({ answers });

                        const res = await calculateRisk();

                        response = res?.Groups?.[0];

                        await setChosenRisk({ id: response?.RiskCategory?.Id, productId: res?.ProductId, submissionId: res?.submissionId });
                    } else {
                        response = fullData;
                    }

                    await handlePostExternalQuestionnaire();

                    if (saveAdditionalData) {
                        await saveAdditionalData({ clientId: mClientId, clientRiskToAccept, response });
                    }

                    if (advisoryProcessInProgressSelector && mClientId && (response?.ScoreRangePercentage || response?.Score) && response?.RiskCategory?.Id) {
                        await saveAdvisoryDataAction({
                            key: 'investor-profile',
                            payload: {
                                ContactId: mClientId,
                                Score: response?.ScoreRangePercentage ? calculateStrategyScore(response?.ScoreRangePercentage) : response?.Score,
                                CalculatedRiskCategoryId: response?.RiskCategory?.Id,
                            },
                        });
                    }
                }));
                setSending(false);
                setIsSubmitted(false);

                stepComplete();
            }
        } catch (err: any) {
            notification.open({
                content: err.message || t('general.somethingWentWrong'),
                type: 'error',
            });
        }
    }, [profiles, isPk, language, saveAdditionalData, advisoryProcessInProgressSelector]);

    const isAnyInvestorProfileLoading = useMemo(() => Object.keys(profiles).map(key => profiles?.[key]?.isLoading)?.some((a) => (a === true)), [profiles]);

    const pageErrors: any = useMemo(() => Object.keys(profiles).find(key => profiles?.[key]?.pageError), [profiles]);

    const { stepComplete } = usePortfolioProcessNavigation({
        saveOnContinue: onContinue,
        enableAutoStepComplete: false,
        backDisabled: isBackDisabled,
        continueInProgress: isAnyInvestorProfileLoading || isSending,
        pageErrors,
    });

    if (isUsersLoading) {
        return <Loader />;
    }

    return (
        <Preloader isLoading={!targetDFSClientId}>
            <InvestorProfileWithProvider
                clientName={targetClientName}
                clientAge={clientAge}
                title={productDescription}
                sessionStorageKey={`create-portfolio/investor-profile/${targetDFSClientId}/data`}
                dfsClientId={targetDFSClientId}
                onQuestionnaireUpdate={onQuestionnaireUpdate}
                isSubmitted={isSubmitted}
                isSending={isSending}
            />
            {isJointAccount && (
                <Infobox className="ja-message">
                    {t('portfolio.createNewPortfolio.investorProfile.jaInfoMessage', { clientFullName: childClientName, clientOwnerFullName: targetClientName })}
                </Infobox>
            )}
        </Preloader>
    );
};
